import { useState, useEffect, useMemo } from 'react'
import { useQuery } from 'react-query'
import { useSelector, useDispatch } from 'react-redux'
import { Redirect, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { t } from 'ttag'

import { api } from '@h2/api'
import { openSigninDialog } from '@main/actions/signinOrRegisterDialog'

import { Skeleton } from './skeleton'
import { Membership } from './membership'
import {MissingDoB} from './missingdob';
import { Pending } from './pending'
import { Finished } from './finished'

import { ParentApproval } from '../panels/parentApproval'
import { usePanel } from '../usePanel'
import { usePageTitle } from '../../hooks/usePageTitle'
import { authorizationCode } from '../../../selectors/authorizationCode'

const panelMap = {
  missingdob: MissingDoB,
  membership: Membership,
  pending: Pending,
  parentApproval: ParentApproval,
  complete: Finished
}
const Join = ({ inDialog = false }) => {
  const dispatch = useDispatch()
  /**
   * A membership `code` can come from the request path or
   * the redux store depending on how the membership was
   * created for the user. For example, 'rostered' or imported
   * users will be redirected upon login to a path that includes
   * the membership code, so they can choose to join the site.
   */
  const { code: paramsCode } = useParams()
  const storeCode = useSelector(authorizationCode)
  const code = useMemo(() => paramsCode ?? storeCode, [paramsCode, storeCode])
  const [redirect, setRedirect] = useState(null)
  const { panel, setPanel, setProperties, properties, renderPanel } = usePanel('')
  const { data: membership, isFetching } = useQuery(
    ['authMembership', code],
    () => api.fetch(`/auth/membership/${code}`),
    {
      onError: () => {
        if (inDialog) {
          dispatch(openSigninDialog('signIn'))
        } else {
          setRedirect({ to: '/signin' })
        }
      }
    }
  )
  const panelProps = useMemo(
    () => ({
      inDialog,
      renderPanel,
      properties: { ...membership, ...properties }
    }),
    [inDialog, membership, properties, renderPanel]
  )
  const Panel = panelMap[panel]


  usePageTitle(t`Consent To Access Site`, inDialog)
  useEffect(() => {
    if (isFetching || !membership) {
      return;
    }

    const { accepted, removed, userCanAccept, authorizedPersonEmail } = membership;

    // Handle accepted membership
    if (accepted && !removed) {
      setPanel('complete');

      return;
    }

    // Handle case where the user can accept membership
    if (userCanAccept) {
      renderPanel('membership');

      return;
    }

    if(userCanAccept === null) {
      renderPanel('missingdob');

      return;
    }

    // Handle cases where the user cannot accept membership
    if (!userCanAccept) {
      if (!authorizedPersonEmail) {
        if (removed) {
          setProperties(prev => ({
            ...prev,
            declinedMsg: t`The authorized person you chose has declined to approve your account. If you would still like to access the site you can make another request.`
          }));
        }

        if (userCanAccept === false) {
          setPanel('parentApproval');
        } else {
          renderPanel('membership');
        }

        return;
      }

      // Show pending panel if email is sent to parent
      renderPanel('pending', { authorizedPersonEmail });

      return;
    }

    // Default to membership panel if no other condition matches
    renderPanel('membership');
  }, [membership, setPanel, setProperties, isFetching, renderPanel]);

  if (redirect) {
    return <Redirect {...redirect} />
  }

  if (Panel) {
    return <Panel {...panelProps} />
  }

  return <Skeleton inDialog={inDialog} />
}

Join.propTypes = {
  inDialog: PropTypes.bool
}

export { Join }
